/**
 * Two sign-in methods, one account.
 *
 * Firebase is configured to allow one account per email address, which is the
 * right setting — the alternative silently creates a second account with the
 * same address and splits a player's puns, crowns and follows across two
 * identities that can never be merged.
 *
 * The cost of that setting is that every crossing between methods becomes an
 * error the app has to handle, and the default Firebase codes are useless to a
 * player: `auth/account-exists-with-different-credential` is not an instruction.
 * Everything here exists to turn those into a next step.
 *
 * The four crossings:
 *
 *   1. Password account, taps Google  → collision. Ask for the password once,
 *      link Google to the account, and Google works from then on.
 *   2. Google account, types password → cannot succeed; there is no password to
 *      match. Say so, and point at the Google button.
 *   3. Google account, asks for a reset → nothing to reset. Same answer, and
 *      the reply must not reveal whether the address is registered at all.
 *   4. Either, from Settings → connect the other method deliberately rather
 *      than by walking into an error.
 */
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  linkWithCredential,
  linkWithPopup,
  signInWithEmailAndPassword,
  type AuthCredential,
  type User,
} from 'firebase/auth';

import { auth } from '../firebase';

/** Provider ids exactly as Firebase reports them on `user.providerData`. */
export const GOOGLE_PROVIDER = 'google.com';
export const PASSWORD_PROVIDER = 'password';

/** Which sign-in methods this account currently has attached. */
export function connectedProviders(user: User): string[] {
  return user.providerData.map((entry) => entry.providerId);
}

/**
 * The Google credential Firebase stashes on a collision error.
 *
 * It is single-use and lives only on that error object, so it has to be caught
 * and carried through the password prompt. Losing it means the player has to
 * start the Google popup again after entering their password.
 */
export function googleCredentialFromError(error: unknown): AuthCredential | null {
  try {
    // The SDK types this as FirebaseError; the catch binding is `unknown`, and
    // the function returns null for anything it does not recognise anyway.
    return GoogleAuthProvider.credentialFromError(error as Parameters<typeof GoogleAuthProvider.credentialFromError>[0]);
  } catch {
    return null;
  }
}

/** The address that collided, which Firebase puts on the error rather than in the message. */
export function collidingEmail(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'customData' in error) {
    const { customData } = error as { customData?: { email?: unknown } };
    if (typeof customData?.email === 'string') return customData.email;
  }
  return '';
}

/**
 * Crossing 1: prove the password, then attach Google to that same account.
 *
 * The order is forced by Firebase — a credential can only be linked to a signed
 * in user, so the password sign-in has to happen first. That is also why this
 * asks for the password at all: without it there is no way to prove the person
 * holding this Google account is the person who owns the password account, and
 * linking on the strength of a matching email address alone would let anyone
 * who can create a Google account with your address take over your puns.
 */
export async function linkGoogleToPasswordAccount(
  email: string,
  password: string,
  credential: AuthCredential,
): Promise<User> {
  const { user } = await signInWithEmailAndPassword(auth, email, password);
  await linkWithCredential(user, credential);
  return user;
}

/** Crossing 4a: attach Google to the account already signed in. */
export async function connectGoogle(user: User): Promise<void> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });

  await linkWithPopup(user, provider);
  // providerData is a snapshot from sign-in time and does not update itself.
  await user.reload();
}

/** Crossing 4b: give an account that only has Google a password as well. */
export async function addPassword(user: User, password: string): Promise<void> {
  if (!user.email) {
    throw new Error('This account has no email address to attach a password to.');
  }

  await linkWithCredential(user, EmailAuthProvider.credential(user.email, password));
  await user.reload();
}

/**
 * What to tell someone whose password sign-in failed.
 *
 * Firebase cannot distinguish "wrong password" from "this account has no
 * password" without an enumeration-leaking lookup, and which code it returns
 * depends on whether email enumeration protection is switched on in the console
 * — `auth/invalid-credential` when it is, `auth/wrong-password` and
 * `auth/user-not-found` when it is not. So all three get one answer that covers
 * both possibilities, which is both more useful and less revealing than any of
 * the codes.
 */
export function passwordSignInMessage(code: string): string {
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-email':
      return 'That email and password do not match. If you made your account with Google, use Continue with Google instead.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'auth/user-disabled':
      return 'That account has been disabled.';
    case 'auth/network-request-failed':
      return 'Could not reach the server. Check your connection and try again.';
    default:
      return '';
  }
}

/**
 * What to say after a password reset request — the same thing every time.
 *
 * Deliberately identical whether the address has an account, has one without a
 * password, or has none at all. The old copy said "No account uses that email",
 * which is an enumeration oracle: anyone could test addresses against it and
 * learn who plays. It was also actively wrong for a Google account, where there
 * is an account and simply no password on it.
 */
export const RESET_REQUESTED_MESSAGE =
  'If that address has a password, the reset email is on its way. Signed up with Google? Use Continue with Google instead.';
